// 嵌入式 WebView 消息(message_type='embed')—— 第四档降级的 web 真源实现。
// spec: docs/superpowers/specs/2026-08-07-native-chat-ui-design.md「补充:嵌入式 WebView 消息组件」
// UX 规范(三端对齐真源): .superpowers/sdd/2026-08-03-marketplace-phase0-1/task-embed-ux-polish.md
//
// 协议 payload:
//   embed_url  https URL(与 embed_html 二选一)→ sandboxed <iframe src>
//   embed_html 内联 HTML(与 embed_url 二选一)→ sandboxed <iframe srcdoc>
//   height     必填 px(服务端已夹 <=480;客户端再夹一道,协议高度固定,不自适应)
//   title      可选
//   fallback_text 必填 —— 老客户端 / 加载失败 / 超时的安全网
//
// 安全红线(spec「红线」节):
//   - sandbox="allow-scripts allow-forms",**不加 allow-same-origin** ——
//     嵌入文档是 opaque origin,跨源访问不了宿主 window/token/localStorage/bridge。
//     embed_html 走 srcdoc 同样是 opaque origin(sandbox 未授 same-origin 时
//     srcdoc 不继承父源),结论一致。
//   - 本组件不挂任何 window 'message' 监听,也绝不向 iframe postMessage——
//     不给嵌入内容留通道。(widget.js 的 handleMessage 有 origin 白名单,
//     sandbox 后 origin 是 'null' 字符串,进不了白名单。)
//   - 高度协议给定 + 双端夹上限:嵌入单元绝不能无限撑高破坏消息列表滚动。
//   - referrerPolicy=no-referrer:不把宿主页 URL 泄给嵌入方。
//
// UX 状态机(三端一致,native 照做):
//   pending(占位,已预留协议高度) → loading(取得并发额度,iframe 开始加载)
//     → ready(onLoad)| failed(onError / 超时 → fallback 卡 + 重试)
//   - 骨架延迟 300ms 才现身:快加载不闪骨架;
//   - 高度从首帧就按协议值预留:加载完不跳变(零 CLS);
//   - 进入视口(rootMargin 200px)才开始加载,且同屏并发 ≤2 —— 与富媒体并发预算同思路;
//   - resize 走高度过渡(--lc-dur-base / --lc-ease-out),不瞬跳;
//   - embed 上报 notify/submit → 容器层即时"已提交"回执条(宿主消息气泡之外的本地反馈);
//   - embed_state 存在 = 服务端权威完成态 → 容器层打"已完成"角标。
//
// 与 native 的关系:本文件是跨端真源,Android/iOS 受控 WebView 单元按同一套
// normalize 语义 + 同一套状态机/视觉规范消费。
// 注意:为了让 node --test 直接 import,本文件不用 JSX(React.createElement)。

import React, { Component } from 'react';
// 刻意不 import ../i18n:那条链有目录导入,node --test 直接跑本文件会断。
// 所有文案由 message-area 以 props 传入(loadingLabel/retryLabel/...),
// 本文件保持零依赖纯渲染单元(也方便 native 侧对照移植)。
import { LANE_EMBED_SHIM_SOURCE } from '../embed-sdk/lane-embed.js';

const h = React.createElement;

/** 与服务端 RichMessageValidator::EMBED_MAX_HEIGHT 一致 */
export const EMBED_MAX_HEIGHT = 480;
/** iframe 加载超时(ms):到点还没 load → 落 fallback_text 卡。从**开始加载**起算,
 *  不是从挂载起算 —— 视口外排队的 embed 不该被算超时。 */
export const EMBED_LOAD_TIMEOUT_MS = 8000;
/** 固定 sandbox 值。禁止扩(尤其禁止 allow-same-origin / allow-top-navigation)。 */
export const EMBED_SANDBOX = 'allow-scripts allow-forms';
/** 骨架延迟出现(ms):加载在这之内完成就完全不显骨架,避免"闪一下"。 */
export const EMBED_SKELETON_DELAY_MS = 300;
/** 交互回执条停留时长(ms)。 */
export const EMBED_ACK_VISIBLE_MS = 2400;
/** 同屏并发加载额度:超出的 embed 排队(仍占位、不闪),前一个 ready/failed 才放行。 */
export const EMBED_MAX_CONCURRENT_LOADS = 2;
/** 懒加载提前量:进入视口前 200px 就开始加载,滚到时通常已就绪。 */
export const EMBED_LAZY_ROOT_MARGIN = '200px';

/**
 * embed_url 只认 https(签名代理产出的媒体 URL 也是 https,同一纪律)。
 */
export function isSafeEmbedUrl(url) {
    if (typeof url !== 'string') return false;
    return /^https:\/\/\S+$/i.test(url.trim());
}

/**
 * 高度夹取:正整数,上限 480。非法(缺失/0/负/NaN)→ null = 拒渲染。
 * "不给则拒渲染落 fallback 卡——不许自适应无限高撑破列表"。
 */
export function clampEmbedHeight(raw) {
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.min(n, EMBED_MAX_HEIGHT);
}

/**
 * payload → 渲染描述。返回 null = 协议不合法,调用方落 fallback_text 卡。
 *
 * 合法条件:height 有效;embed_url / embed_html 恰好给一个;url 模式必须 https。
 *
 * @returns {null | {mode:'url'|'html', src:?string, html:?string, height:number,
 *                   title:string, fallbackText:string}}
 */
export function normalizeEmbedPayload(payload) {
    if (!payload || typeof payload !== 'object') return null;

    const height = clampEmbedHeight(payload.height);
    if (height == null) return null;

    const url = typeof payload.embed_url === 'string' ? payload.embed_url.trim() : '';
    const html = typeof payload.embed_html === 'string' ? payload.embed_html : '';
    const hasUrl = url !== '';
    const hasHtml = html.trim() !== '';
    if (hasUrl === hasHtml) return null;          // 两个都给 / 都没给 → 非法
    if (hasUrl && !isSafeEmbedUrl(url)) return null;

    return {
        mode: hasUrl ? 'url' : 'html',
        src: hasUrl ? url : null,
        html: hasHtml ? html : null,
        height,
        title: typeof payload.title === 'string' ? payload.title : '',
        fallbackText: typeof payload.fallback_text === 'string' ? payload.fallback_text : '',
    };
}

/**
 * 完成态判定(三端同一条规则):**embed_state 存在即完成态** —— 服务端只在
 * 交互被权威裁决后才回写 embed_state。唯一例外是显式 completed === false
 * (给"进行中但有中间态"的组件留的后门)。
 * 本地刚提交、服务端还没回写 → **不**算完成(完成态不做乐观更新,本地反馈由
 * 回执条承担)。
 */
export function isEmbedCompleted(embedState) {
    if (!embedState || typeof embedState !== 'object') return false;
    if (embedState.completed === false) return false;
    return true;
}

/**
 * 并发加载调度器:同屏 embed 最多 max 个同时加载,其余排队(占位不闪)。
 * 纯逻辑、可注入,便于测试与 native 对照移植。
 */
export function createEmbedLoadScheduler(max = EMBED_MAX_CONCURRENT_LOADS) {
    const limit = Math.max(1, Math.floor(Number(max) || 1));
    let active = 0;
    const queue = [];

    function pump() {
        while (active < limit && queue.length) {
            const job = queue.shift();
            if (job.cancelled) continue;
            active += 1;
            job.started = true;
            try { job.run(); } catch (e) { /* 单个 embed 起不来不牵连队列 */ }
        }
    }

    return {
        /** @returns {function} release —— ready/failed/unmount 时必须调用一次。 */
        acquire(run) {
            const job = { run, cancelled: false, started: false, released: false };
            queue.push(job);
            pump();
            return function release() {
                if (job.released) return;
                job.released = true;
                if (job.started) { active = Math.max(0, active - 1); pump(); }
                else { job.cancelled = true; }
            };
        },
        stats() { return { active, queued: queue.filter((j) => !j.cancelled).length, limit }; },
    };
}

/** 进程内默认调度器(同一 widget 内所有 embed 共用一份额度)。 */
export const embedLoadScheduler = createEmbedLoadScheduler(EMBED_MAX_CONCURRENT_LOADS);

// ---- 视觉:全部走结构 token(圆角/发丝线/阴影/间距/时长/曲线) ------------------
// 嵌入内容自带样式,但**外框是宿主的**;暗色由 --lc-surface/--lc-text 族自动跟随,
// 本文件不写任何硬编码颜色(唯一例外是 currentColor 图标)。

const CONTAINER_STYLE = {
    background: 'var(--lc-surface, #ffffff)',
    border: 'var(--lc-hairline)',
    borderRadius: 'var(--lc-radius-md)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '320px',
    boxShadow: 'var(--lc-shadow-1)',
    position: 'relative',
    // 滚动隔离(规范 §5.7):嵌入内容滚到边界后,浏览器默认会把剩余的滚动量
    // **链**给最近的可滚祖先 —— 这里就是消息列表。表现是"在小组件里滑两下,
    // 整个会话列表跟着乱跳"。contain 只截断链式传导,不影响 embed 自身滚动,
    // 也不像 none 那样连移动端的下拉刷新一并禁掉。
    overscrollBehavior: 'contain',
};

const HEADER_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--lc-space-2)',
    padding: 'var(--lc-space-2) var(--lc-space-3)',
    borderBottom: 'var(--lc-hairline)',
    minHeight: '20px',
};

const TITLE_STYLE = {
    flex: '1 1 auto',
    minWidth: 0,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.35,
    color: 'var(--lc-text, #1e293b)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

const BADGE_STYLE = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--lc-space-1)',
    flexShrink: 0,
    padding: '2px 8px',
    borderRadius: 'var(--lc-radius-full)',
    background: 'var(--lc-surface-2, #f1f5f9)',
    color: 'var(--lc-text-2, #64748b)',
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: 1.6,
};

/** 组件内部样式表:keyframes / hover / focus-visible / reduced-motion。
 *  inline style 表达不了这几类,统一在这一块里(与 giphy.js 同法)。 */
export const EMBED_STYLE_TEXT = [
    '@keyframes lcEmbedSkeleton { 0%, 100% { opacity: .55; } 50% { opacity: 1; } }',
    '@keyframes lcEmbedAckIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }',
    '.lc-embed__bar { animation: lcEmbedSkeleton 1.4s ease-in-out infinite; }',
    '.lc-embed__ack { animation: lcEmbedAckIn var(--lc-dur-fast, 150ms) var(--lc-ease-out, ease-out) both; }',
    '.lc-embed__retry:hover { background: var(--lc-surface-2, #f1f5f9); }',
    '.lc-embed__retry:focus-visible { outline: 2px solid var(--lc-text-2, #64748b); outline-offset: 2px; }',
    '.lc-embed__frame:focus-visible { outline: 2px solid var(--lc-text-2, #64748b); outline-offset: -2px; }',
    '@media (prefers-reduced-motion: reduce) {',
    '  .lc-embed__bar, .lc-embed__ack { animation: none !important; }',
    '  .lc-embed__wrap { transition: none !important; }',
    '}',
].join('\n');

function styleTag() {
    return h('style', { key: 'lc-embed-style' }, EMBED_STYLE_TEXT);
}

/** 16px 描边图标(currentColor,跟随容器文字色 → 暗色自动适配)。 */
function icon(paths, size = 14) {
    return h('svg', {
        width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
        stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round',
        strokeLinejoin: 'round', 'aria-hidden': 'true',
        style: { display: 'block', flexShrink: 0 },
    }, paths.map((d, i) => h('path', { key: i, d })));
}

const ICON_CHECK = ['M20 6 9 17l-5-5'];
const ICON_ALERT = ['M12 9v4', 'M12 17h.01', 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z'];

/**
 * 失败/超时/协议非法时的 fallback_text 卡。导出给 message-area 复用
 * (payload 非法但 fallback_text 在时直接渲染这张卡)。
 *
 * 视觉与其它富消息卡同族:同一表面/发丝线/圆角/阴影,左侧告警图标点题,
 * 文案友好(服务端给的 fallback_text 就是"人话版本"),可选「重试」重载 iframe。
 */
export function EmbedFallbackCard({ title, text, onRetry, retryLabel }) {
    return h('div', {
        className: 'lc-embed lc-embed--fallback',
        style: CONTAINER_STYLE,
        role: 'group',
        'aria-label': title || undefined,
    },
        styleTag(),
        h('div', { style: { display: 'flex', gap: 'var(--lc-space-2)', padding: 'var(--lc-space-3)' } },
            h('span', {
                style: { color: 'var(--lc-text-3, #94a3b8)', marginTop: '2px', display: 'flex', flexShrink: 0 },
            }, icon(ICON_ALERT, 16)),
            h('div', { style: { minWidth: 0, flex: '1 1 auto' } },
                title ? h('div', {
                    style: {
                        fontSize: '14px', fontWeight: 600, lineHeight: 1.35,
                        color: 'var(--lc-text, #1e293b)', marginBottom: 'var(--lc-space-1)',
                    },
                }, title) : null,
                h('p', {
                    style: {
                        margin: 0, fontSize: '13px', lineHeight: 1.5,
                        color: 'var(--lc-text-2, #64748b)', whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                    },
                }, text || ''),
                typeof onRetry === 'function' ? h('button', {
                    type: 'button',
                    className: 'lc-embed__retry',
                    onClick: onRetry,
                    style: {
                        marginTop: 'var(--lc-space-2)',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        color: 'var(--lc-text, #1e293b)',
                        background: 'transparent',
                        border: 'var(--lc-hairline)',
                        borderRadius: 'var(--lc-radius-sm)',
                        cursor: 'pointer',
                        transition: 'background var(--lc-dur-fast, 150ms) var(--lc-ease-out, ease-out)',
                    },
                }, retryLabel || 'Retry') : null,
            ),
        ),
    );
}

/**
 * 嵌入渲染单元。
 * props:
 *   payload            原始 payload(内部 normalize)
 *   fallbackText       信封级 fallback(payload.fallback_text 缺席时兜底)
 *   loadingLabel/retryLabel/completedLabel/contentLabel/ackLabel   文案(i18n 由调用方注入)
 *   bridge             embed 交互 bridge(见 embed-bridge.js)
 *   embedMessageId / theme / lang / mainColor / embedState        bridge init 上下文
 *   timeoutMs / skeletonDelayMs / ackVisibleMs / scheduler / visibilityGate  测试注入
 *
 * 状态机: pending → loading → ready | failed(可重试回 pending)
 */
export default class EmbedMessage extends Component {
    constructor(props) {
        super(props);
        // heightOverride:embed 通过 bridge resize 请求的高度(已由 bridge 夹 ≤480);
        // collapsed:embed 请求 close 后收起;attempt:重试计数(换 iframe key 强制重载)。
        this.state = {
            phase: 'pending',
            heightOverride: null,
            collapsed: false,
            skeletonDue: false,
            attempt: 0,
            ack: null,
            resizing: false,
        };
        this._timer = null;
        this._skeletonTimer = null;
        this._ackTimer = null;
        this._resizeTimer = null;
        this._release = null;
        this._unobserve = null;
        this._done = false;
        this._unmounted = false;
        this._registered = false;
        this.containerEl = null;
        this.iframeRef = null;
        this.handleLoad = this.handleLoad.bind(this);
        this.handleError = this.handleError.bind(this);
        this.handleRetry = this.handleRetry.bind(this);
        this._setContainerRef = (el) => { if (el) this.containerEl = el; };
        this._setIframeRef = (el) => {
            this.iframeRef = el;
            if (el) {
                // ref 一挂上 contentWindow 就可用,且早于 iframe 内脚本发 ready ——
                // 必须在 ready 到达前完成注册,否则 bridge 认不出这个 source。
                this._registerBridge(normalizeEmbedPayload(this.props.payload));
            } else {
                this._unregisterBridge();
            }
        };
    }

    componentDidMount() {
        // 骨架延迟:从挂载起算 —— 排队/加载都算"等待",300ms 内结束就完全不显骨架。
        const delay = this._num(this.props.skeletonDelayMs, EMBED_SKELETON_DELAY_MS);
        this._skeletonTimer = setTimeout(() => {
            this._skeletonTimer = null;
            if (!this._unmounted && !this._done) this.setState({ skeletonDue: true });
        }, delay);
        this._gateVisibility();
    }

    componentWillUnmount() {
        this._unmounted = true;
        this._clearTimers();
        this._releaseSlot();
        if (this._unobserve) { try { this._unobserve(); } catch (e) { /* noop */ } this._unobserve = null; }
        this._done = true;
        this._unregisterBridge();
    }

    _num(v, dflt) {
        const n = Number(v);
        return Number.isFinite(n) && n >= 0 ? n : dflt;
    }

    _clearTimers() {
        if (this._timer) { clearTimeout(this._timer); this._timer = null; }
        if (this._skeletonTimer) { clearTimeout(this._skeletonTimer); this._skeletonTimer = null; }
        if (this._ackTimer) { clearTimeout(this._ackTimer); this._ackTimer = null; }
        if (this._resizeTimer) { clearTimeout(this._resizeTimer); this._resizeTimer = null; }
    }

    _releaseSlot() {
        if (this._release) { try { this._release(); } catch (e) { /* noop */ } this._release = null; }
    }

    // ---- 懒加载门:进入视口(提前 200px)才开始加载 ----
    // 没有 DOM / 没有 IntersectionObserver(老浏览器、无头测试)→ 立即放行,
    // 懒加载是**优化**不是前提,任何环境下都不能因此不加载。
    _gateVisibility() {
        const gate = this.props.visibilityGate;
        if (typeof gate === 'function') {
            this._unobserve = gate(this.containerEl, () => this._requestSlot());
            return;
        }
        const el = this.containerEl;
        const IO = (typeof window !== 'undefined' && window.IntersectionObserver)
            || (typeof IntersectionObserver !== 'undefined' ? IntersectionObserver : null);
        if (!el || !IO) { this._requestSlot(); return; }
        try {
            const io = new IO((entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    io.disconnect();
                    this._unobserve = null;
                    this._requestSlot();
                }
            }, { rootMargin: EMBED_LAZY_ROOT_MARGIN, threshold: 0 });
            io.observe(el);
            this._unobserve = () => io.disconnect();
        } catch (e) {
            this._requestSlot();
        }
    }

    // ---- 并发额度:拿到槽位才真正开始加载 ----
    _requestSlot() {
        if (this._unmounted || this._release) return;
        const scheduler = this.props.scheduler || embedLoadScheduler;
        this._release = scheduler.acquire(() => this._beginLoad());
    }

    _beginLoad() {
        if (this._unmounted || this._done) return;
        const ms = this._num(this.props.timeoutMs, EMBED_LOAD_TIMEOUT_MS);
        // 超时从**开始加载**起算(不含排队/视口外等待)。
        this._timer = setTimeout(() => {
            this._timer = null;
            if (!this._done) {
                this._done = true;
                this._releaseSlot();
                this.setState({ phase: 'failed' });
            }
        }, ms);
        this.setState({ phase: 'loading' });
    }

    // ---- Embed 交互 bridge 注册 ----
    // sandbox 无 allow-same-origin ⇒ iframe 是 opaque origin,它 postMessage 上来的
    // event.source === 这个 iframe 的 contentWindow(不可伪造),就是 bridge 的强绑定。
    // resize/close/event 是本组件的本地态(改高度 / 收起 / 回执条);event/notify/submit/
    // openUrl 的**宿主副作用**由 chat.js 注入的 deps 处理(转 postback / 走 linkPolicy)。
    // init 载荷携带 theme/lang/mainColor + embedState(服务端权威完成态),绝不含 token/PII。
    _bridgeId() {
        const mid = this.props.embedMessageId;
        return mid != null ? ('embed-' + mid) : ('embed-anon-' + (this._anonId || (this._anonId = Math.random().toString(36).slice(2))));
    }

    _initContext() {
        return {
            theme: this.props.theme === 'dark' ? 'dark' : 'light',
            lang: this.props.lang || null,
            mainColor: this.props.mainColor || null,
            state: this.props.embedState || null,
        };
    }

    _registerBridge(norm) {
        const bridge = this.props.bridge;
        if (!bridge || this._registered) return;
        const win = this.iframeRef && this.iframeRef.contentWindow;
        if (!win) return;
        bridge.register({
            id: this._bridgeId(),
            win,
            src: norm && norm.mode === 'url' ? norm.src : null,
            embedMessageId: this.props.embedMessageId != null ? this.props.embedMessageId : null,
            initContext: this._initContext(),
            onResize: (px) => this._applyResize(px),
            onClose: () => this.setState({ collapsed: true }),
            onEvent: (name, data) => this._showAck(name, data),
        });
        this._registered = true;
    }

    _unregisterBridge() {
        if (this._registered && this.props.bridge) {
            try { this.props.bridge.unregister(this._bridgeId()); } catch (e) { /* best-effort */ }
        }
        this._registered = false;
    }

    /** resize:高度带过渡(--lc-dur-base)。resizing 标志只用于过渡期间关掉 iframe
     *  的指针事件,避免内容在动画中被误点。 */
    _applyResize(px) {
        if (this._unmounted || this.state.phase === 'failed') return;
        if (px === this.state.heightOverride) return;
        this.setState({ heightOverride: px, resizing: true });
        if (this._resizeTimer) clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
            this._resizeTimer = null;
            if (!this._unmounted) this.setState({ resizing: false });
        }, 240); // ≈ --lc-dur-base + 余量
    }

    /** 交互回执:embed 上报 notify/submit 后,容器层给一条即时可见的确认。
     *  文案优先用事件自带 label(与 chat.js 转 postback 时的气泡文案同源)。 */
    _showAck(name, data) {
        if (this._unmounted) return;
        const label = (data && typeof data.label === 'string' && data.label.trim())
            ? data.label.trim().slice(0, 80)
            : (this.props.ackLabel || 'Submitted');
        // 用 updater 取上一次的序号:连点两次也能换 key 重播入场动画。
        this.setState((s) => ({ ack: { text: label, seq: (s.ack ? s.ack.seq + 1 : 1), name } }));
        if (this._ackTimer) clearTimeout(this._ackTimer);
        this._ackTimer = setTimeout(() => {
            this._ackTimer = null;
            if (!this._unmounted) this.setState({ ack: null });
        }, this._num(this.props.ackVisibleMs, EMBED_ACK_VISIBLE_MS));
    }

    handleLoad() {
        // iframe 一 load 就注册 bridge(此时 contentWindow 可用),即便后面判 ready 失败
        // 也无妨——注册只建立 source→entry 绑定,ready 握手才真正回 init。
        if (this._done) return;
        this._done = true;
        if (this._timer) { clearTimeout(this._timer); this._timer = null; }
        if (this._skeletonTimer) { clearTimeout(this._skeletonTimer); this._skeletonTimer = null; }
        this._releaseSlot();
        this.setState({ phase: 'ready', skeletonDue: false });
    }

    handleError() {
        if (this._done) return;
        this._done = true;
        if (this._timer) { clearTimeout(this._timer); this._timer = null; }
        this._releaseSlot();
        this.setState({ phase: 'failed' });
    }

    /** 重试:整块 iframe 重建(换 key),重新排队走一遍加载。 */
    handleRetry() {
        if (this._unmounted) return;
        this._clearTimers();
        this._unregisterBridge();
        this._releaseSlot();
        this._done = false;
        this.setState(
            (s) => ({ phase: 'pending', attempt: s.attempt + 1, heightOverride: null, skeletonDue: true, ack: null }),
            () => this._requestSlot(),
        );
    }

    _renderSkeleton(height) {
        // 骨架尺寸=协议高度(容器已预留),内容是三条占位条 —— 形似"标题+两行",
        // 不假装成具体组件。延迟 300ms 才 opacity 1,快加载完全看不到它。
        const bar = (w, mt) => h('div', {
            className: 'lc-embed__bar',
            style: {
                height: '10px', width: w, marginTop: mt,
                borderRadius: 'var(--lc-radius-xs)',
                background: 'var(--lc-surface-3, #e2e8f0)',
            },
        });
        return h('div', {
            className: 'lc-embed__loading',
            'aria-hidden': 'true',
            style: {
                position: 'absolute',
                inset: 0,
                padding: 'var(--lc-space-3)',
                background: 'var(--lc-surface-2, #f1f5f9)',
                pointerEvents: 'none',
                opacity: this.state.skeletonDue ? 1 : 0,
                transition: 'opacity var(--lc-dur-fast, 150ms) var(--lc-ease-out, ease-out)',
                overflow: 'hidden',
            },
        },
            bar('62%', 0),
            height >= 120 ? bar('100%', 'var(--lc-space-3)') : null,
            height >= 120 ? bar('84%', 'var(--lc-space-2)') : null,
        );
    }

    /** 加载中的屏幕阅读器播报(骨架是纯装饰 aria-hidden,状态得有人说)。 */
    _renderLoadingStatus() {
        return h('span', {
            className: 'lc-embed__sr',
            role: 'status',
            'aria-live': 'polite',
            style: {
                position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px',
                overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
            },
        }, this.props.loadingLabel || 'Loading…');
    }

    _renderAck() {
        const ack = this.state.ack;
        if (!ack) return null;
        return h('div', {
            className: 'lc-embed__ack',
            key: 'ack-' + ack.seq,
            role: 'status',
            'aria-live': 'polite',
            style: {
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--lc-space-2)',
                padding: 'var(--lc-space-2) var(--lc-space-3)',
                borderTop: 'var(--lc-hairline)',
                background: 'var(--lc-surface-2, #f1f5f9)',
                color: 'var(--lc-text-2, #64748b)',
                fontSize: '12px',
                lineHeight: 1.4,
            },
        }, icon(ICON_CHECK, 13), h('span', {
            style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        }, ack.text));
    }

    _renderHeader(norm, completed) {
        // 头部只在"有话可说"时出现:有标题,或服务端已判定完成。
        // 没有标题也没完成 → 不加这条,embed 内容顶到卡片边缘(嵌入内容自己是主体)。
        if (!norm.title && !completed) return null;
        return h('div', { className: 'lc-embed__header', style: HEADER_STYLE },
            norm.title ? h('div', { className: 'lc-embed__title', style: TITLE_STYLE, title: norm.title }, norm.title) : h('span', { style: { flex: '1 1 auto' } }),
            completed ? h('span', { className: 'lc-embed__badge', style: BADGE_STYLE },
                icon(ICON_CHECK, 12),
                h('span', null, this.props.completedLabel || 'Done'),
            ) : null,
        );
    }

    render() {
        const norm = normalizeEmbedPayload(this.props.payload);
        if (!norm) {
            const p = this.props.payload || {};
            return h(EmbedFallbackCard, {
                title: typeof p.title === 'string' ? p.title : '',
                text: (typeof p.fallback_text === 'string' && p.fallback_text)
                    ? p.fallback_text : (this.props.fallbackText || ''),
            });
        }

        if (this.state.phase === 'failed') {
            // payload.fallback_text 优先(协议必填);信封级 fallback(props)兜底;
            // 都没有时用 i18n 的通用失败文案(绝不出现空卡)。
            return h(EmbedFallbackCard, {
                title: norm.title,
                text: norm.fallbackText || this.props.fallbackText || this.props.failedLabel || '',
                onRetry: this.handleRetry,
                retryLabel: this.props.retryLabel,
            });
        }

        // embed 请求 close → 收起(只收起该 embed,不驱动宿主做别的)。
        if (this.state.collapsed) return null;

        // 高度:默认协议给定的固定高度;若 embed 通过 bridge resize 请求过高度
        // (bridge 已夹 ≤480),用请求值。永不无限撑破消息列表滚动。
        const appliedHeight = (this.state.heightOverride != null ? this.state.heightOverride : norm.height);
        const loading = this.state.phase !== 'ready';
        const completed = isEmbedCompleted(this.props.embedState);
        const label = norm.title || this.props.contentLabel || 'Embedded content';

        const iframeProps = {
            className: 'lc-embed__frame',
            key: 'frame-' + this.state.attempt,
            ref: this._setIframeRef,
            sandbox: EMBED_SANDBOX,
            title: label,
            referrerPolicy: 'no-referrer',
            loading: 'lazy',
            style: {
                display: 'block',
                width: '100%',
                height: '100%',
                border: '0',
                // 就绪前压在骨架下面,ready 时淡入;骨架卸载与淡入同一档时长,不闪。
                opacity: loading ? 0 : 1,
                transition: 'opacity var(--lc-dur-fast, 150ms) var(--lc-ease-out, ease-out)',
                pointerEvents: this.state.resizing ? 'none' : 'auto',
            },
            onLoad: this.handleLoad,
            onError: this.handleError,
        };
        if (norm.mode === 'url') {
            // embed_url(第三方独立页):作者自己 <script src=cdn/embed/lane-embed.js> 接入
            // (软依赖,拉不到软降级)。我们不改人家的页,不注入。
            iframeProps.src = norm.src;
        } else {
            // srcdoc(我们自己生成的 embed_html):零外链 —— 编译期把垫片源码内联进
            // srcdoc 的 <script>,window.LaneEmbed 首帧就位,embed 作者直接用,不引 CDN。
            iframeProps.srcDoc = '<script>' + LANE_EMBED_SHIM_SOURCE + '</script>\n' + norm.html;
        }

        return h('div', {
            className: 'lc-embed',
            ref: this._setContainerRef,
            style: CONTAINER_STYLE,
            role: 'group',
            'aria-label': label,
            'aria-busy': loading ? 'true' : 'false',
        },
            styleTag(),
            this._renderHeader(norm, completed),
            h('div', {
                className: 'lc-embed__wrap',
                style: {
                    position: 'relative',
                    // 高度从首帧就按协议值预留 → 加载完不跳变;resize 时平滑过渡。
                    height: appliedHeight + 'px',
                    transition: 'height var(--lc-dur-base, 200ms) var(--lc-ease-out, ease-out)',
                    background: 'var(--lc-surface, #ffffff)',
                },
            },
                // pending 阶段(视口外 / 排队中)不挂 iframe:占位撑住高度即可,
                // 真正开始加载才创建,这就是并发预算能生效的原因。
                this.state.phase === 'pending' ? null : h('iframe', iframeProps),
                loading ? this._renderSkeleton(appliedHeight) : null,
                loading ? this._renderLoadingStatus() : null,
            ),
            this._renderAck(),
        );
    }
}
